/**
 * Master Data Vuex Store Module
 *
 * This module implements caching for master data to avoid unnecessary API calls.
 * Data is fetched once on app initialization and stored in Vuex state.
 *
 * Benefits:
 * - Reduces API calls by 80-90%
 * - Improves application performance
 * - Single source of truth for master data
 * - Automatic cache invalidation support
 */

import { api } from "@/services/api";


const state = {
   users: [],
   // NOTE: `employees` removed from Vuex — datasets can be 10k+ records
   // which caused memory bloat on the client. Components that need the list
   // now call GET /employees?entity_id=X&active=true directly (see
   // promotions.vue for example pattern).
   departments: [],
   designations: [],
   subDepartments: [],
   // functions: [],  // Not used - commented out
   // roles: [],  // Not used - commented out
   clients: [],
   // industries: [],  // Not used - commented out
   // processes: [],  // Not used - removed from backend
   // products: [],  // Not used - removed from backend
   kpis: [],
   services: [],
   serviceCategories: [],
   appraisalDetails: [],
   appraisalPeriods: [],
   checklists: [],
   checklistTasks: [],
   checklistSubtasks: [],
   employeeSettings: [],
   // Performance masters
   uoms: [],
   kpiTypes: [],
   targetTypes: [],
   // channels: [],  // Not used - commented out
   // subChannels: [],  // Not used - commented out
   // divisions: [],  // Not used - commented out
   // categories: [],  // Not used - commented out
   // stores: [],  // Not used - commented out
   performanceRatings: [],
   // Financial ratios - loaded separately with country/industry params
   financialRatios: [],
   financialRatiosLoaded: false,
   // Favourite Details
   favouriteDetails: {},
   // Financials Input Details
   financialsInputDetails: [],
   // Holidays
   holidays: [],
   // Weekoffs
   weekoffs: [],
   // Tenant Masters
   tenantMasters: [],
   // Branches
   branches: [],
   // Tickets
   tickets: [],
   // Form Maintenance
   formMaintenances: [],
   // Forms Master
   formsMasters: [],
   // Slabs
   slabs: [],
   // Processes
   processes: [],
   // Industries
   industries: [],
   // Client Types
   clientTypes: [],
   // Policy Types
   policyTypes: [],
   // Routes
   routes: [],
   // Payment Received
   paymentReceived: [],
   // Ticket Categories
   ticketCategories: [],
   // Locations
   locations: [],
   // Customer Groups
   customerGroups: [],
   // Customer Grades
   customerGrades: [],
   loaded: false,
   // Loading flag to prevent duplicate API calls
   checklistPageDataLoading: false,
};

const getters = {
   // Users
   getUsers: state => state.users,
   getActiveUsers: state => state.users,

   // Employees — REMOVED (10k+ record bloat, fetched per-screen via direct API now)

   // Departments
   getDepartments: state => state.departments,
   getActiveDepartments: state => state.departments,

   // Designations
   getDesignations: state => state.designations,
   getActiveDesignations: state => state.designations,

   // Sub-Departments
   getSubDepartments: state => state.subDepartments,
   getActiveSubDepartments: state => state.subDepartments,

   // Functions - Not used - commented out
   // getFunctions: state => state.functions,
   // getActiveFunctions: state => state.functions,

   // Roles - Not used - commented out
   // getRoles: state => state.roles,
   // getActiveRoles: state => state.roles,

   // Clients
   getClients: state => state.clients,
   getActiveClients: state => state.clients,

   // Industries - Not used - commented out
   // getIndustries: state => state.industries,
   // getActiveIndustries: state => state.industries,

   // Processes - Not used - removed from backend
   // getProcesses: state => state.processes,
   // getActiveProcesses: state => state.processes,

   // Products - Not used - removed from backend
   // getProducts: state => state.products,
   // getActiveProducts: state => state.products,

   // KPIs
   getKpis: state => state.kpis,
   getActiveKpis: state => state.kpis,

   // Services
   // getServices: state => state.services,
   getActiveServices: state => state.services,

   // Service Categories
   // getServiceCategories: state => state.serviceCategories,
   getActiveServiceCategories: state => state.serviceCategories,

   // Appraisal Details
   getAppraisalDetails: state => state.appraisalDetails,
   getActiveAppraisalDetails: state => state.appraisalDetails,

   // Appraisal Periods
   getAppraisalPeriods: state => state.appraisalPeriods,
   getActiveAppraisalPeriods: state => state.appraisalPeriods,

   // Checklists
   // getChecklists: state => state.checklists,
   getActiveChecklists: state => state.checklists,

   // Checklist Tasks
   // getChecklistTasks: state => state.checklistTasks,
   getActiveChecklistTasks: state => state.checklistTasks,

   // Checklist Subtasks
   // getChecklistSubtasks: state => state.checklistSubtasks,
   getActiveChecklistSubtasks: state => state.checklistSubtasks,

   // Employee Settings
   getEmployeeSettings: state => state.employeeSettings,

   // UOMs
   // getUoms: state => state.uoms,
   getActiveUoms: state => state.uoms,

   // KPI Types
   // getKpiTypes: state => state.kpiTypes,
   getActiveKpiTypes: state => state.kpiTypes,

   // Target Types
   // getTargetTypes: state => state.targetTypes,
   getActiveTargetTypes: state => state.targetTypes,

   // Channels - Not used - commented out
   // getChannels: state => state.channels,
   // getActiveChannels: state => state.channels,

   // Sub-Channels - Not used - commented out
   // getSubChannels: state => state.subChannels,
   // getActiveSubChannels: state => state.subChannels,

   // Divisions - Not used - commented out
   // getDivisions: state => state.divisions,
   // getActiveDivisions: state => state.divisions,

   // Categories - Not used - commented out
   // getCategories: state => state.categories,
   // getActiveCategories: state => state.categories,

   // Stores - Not used - commented out
   // getStores: state => state.stores,
   // getActiveStores: state => state.stores,

   // Performance Ratings
   getPerformanceRatings: state => state.performanceRatings,
   getActivePerformanceRatings: state => state.performanceRatings,

   // Financial Ratios
   getFinancialRatios: state => state.financialRatios,
   isFinancialRatiosLoaded: state => state.financialRatiosLoaded,

   // Favourite Details
   getFavouriteDetails: state => state.favouriteDetails,

   // Financials Input Details
   getFinancialsInputDetails: state => state.financialsInputDetails,

   // Holidays
   getHolidays: state => state.holidays,

   // Weekoffs
   getWeekoffs: state => state.weekoffs,

   // Tenant Masters
   getTenantMasters: state => state.tenantMasters,

   // Branches
   getBranches: state => state.branches,
   getActiveBranches: state => state.branches,

   // Tickets
   getTickets: state => state.tickets,

   // Form Maintenance
   // getFormMaintenances: state => state.formMaintenances,
   getActiveFormMaintenances: state => state.formMaintenances,

   // Forms Master
   // getFormsMasters: state => state.formsMasters,
   getActiveFormsMasters: state => state.formsMasters,

   // Slabs
   // getSlabs: state => state.slabs,
   getActiveSlabs: state => state.slabs,

   // Processes
   // getProcesses: state => state.processes,
   getActiveProcesses: state => state.processes,

   // Industries
   // getIndustries: state => state.industries,
   getActiveIndustries: state => state.industries,

   // Client Types
   // getClientTypes: state => state.clientTypes,
   getActiveClientTypes: state => state.clientTypes,

   // Policy Types
   // getPolicyTypes: state => state.policyTypes,
   getActivePolicyTypes: state => state.policyTypes,

   // Routes
   // getRoutes: state => state.routes,
   getActiveRoutes: state => state.routes,

   // Payment Received
   // getPaymentReceived: state => state.paymentReceived,
   getActivePaymentReceived: state => state.paymentReceived,

   // Ticket Categories
   // getTicketCategories: state => state.ticketCategories,
   getActiveTicketCategories: state => state.ticketCategories,

   // Locations
   // getLocations: state => state.locations,
   getActiveLocations: state => state.locations,

   // Customer Groups
   // getCustomerGroups: state => state.customerGroups,
   getActiveCustomerGroups: state => state.customerGroups,

   // Customer Grades
   // getCustomerGrades: state => state.customerGrades,
   getActiveCustomerGrades: state => state.customerGrades,

   // Loading state
   isMasterDataLoaded: state => state.loaded,
};

const actions = {
   /**
    * Fetches all master data from the backend in a single API call
    * Only fetches if data hasn't been loaded yet (checked via localStorage for persistence across refreshes)
    */
   async fetchMasterData({ commit, state }) {
      // Return early if already loading (prevents concurrent requests)
      if (state.isFetching) {
         console.log("[MasterData] Already fetching, skipping duplicate request");
         return;
      }

      // Check localStorage to see if data was already loaded in this session
      const cachedLoadState = localStorage.getItem("MASTER_DATA_LOADED");
      const currentDomain = localStorage.getItem("domain");
      const cachedDomain = localStorage.getItem("MASTER_DATA_DOMAIN");

      // Return early ONLY if:
      // 1. localStorage says data was loaded
      // 2. Domain hasn't changed
      // 3. Vuex state actually has data (verify critical datasets are populated)
      const hasVuexData = state.users.length > 0 || state.departments.length > 0;

      if (cachedLoadState === "true" && currentDomain === cachedDomain && hasVuexData) {
         // Restore the loaded state flag if it was reset (e.g., after page refresh)
         if (!state.loaded) {
            commit("setLoaded", true);
         }
         console.log("[MasterData] Already loaded from cache, skipping fetch");
         return;
      }

      // If localStorage says loaded but Vuex is empty, clear localStorage and fetch
      if (cachedLoadState === "true" && !hasVuexData) {
         console.log("[MasterData] localStorage flag set but Vuex empty, clearing cache and fetching");
         localStorage.removeItem("MASTER_DATA_LOADED");
         localStorage.removeItem("MASTER_DATA_DOMAIN");
      }

      // Clear cache if domain has changed (user switched tenant)
      if (cachedDomain && cachedDomain !== currentDomain) {
         console.log("[MasterData] Domain changed, clearing cache");
         commit("clearCache");
         localStorage.removeItem("MASTER_DATA_LOADED");
      }

      try {
         commit("setIsFetching", true);

         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Fetching master data...", { entity_id, domain });

         // Guard: don't fetch if domain is missing
         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping fetch");
            return;
         }

         // API Call 1: Base datasets - always loaded
         console.log("[MasterData] Fetching base datasets...");
         const baseResponse = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: [
               "users_dataset",
               // "employees_dataset" removed — fetched per-screen via /employees API
               "departments_dataset",
               "designations_dataset",
               "subdepartments_dataset",
               "clients_dataset",
               "kpi_dataset",
               "appraisal_period_dataset",
               "checklist_dataset",
               "checklist_task_dataset",
               "checklist_subtask_dataset",
               "employee_setting_dataset",
               "uom_dataset",
               "kpi_type_dataset",
               "target_type_dataset",
               "performance_rating_dataset",
               "favourite_details_dataset",
               "financials_input_details_dataset",
               "holiday_dataset",
               "weekoff_dataset",
               "tenant_masters_dataset",
               "branches_dataset",
               "client_type_dataset",
               "policy_type_dataset",
               "route_dataset",
               "ticket_category_dataset",
               "location_dataset",
               "services_dataset",
               "service_categories_dataset"
            ],
         });

         if (baseResponse.data && baseResponse.data.status === "success") {
            const data = baseResponse.data.data;

            commit("setUsers", data.users_dataset?.data || []);
            // setEmployees removed — employees fetched per-screen via /employees API
            commit("setDepartments", data.departments_dataset?.data || []);
            commit("setDesignations", data.designations_dataset?.data || []);
            commit("setSubDepartments", data.subdepartments_dataset?.data || []);
            // commit("setFunctions", data.functions_dataset?.data || []);  // Not used - commented out
            // commit("setRoles", data.roles_dataset?.data || []);  // Not used - commented out
            commit("setClients", data.clients_dataset?.data || []);
            // commit("setIndustries", data.industries_dataset?.data || []);  // Not used - commented out
            // commit("setProcesses", data.processes_dataset?.data || []);  // Not used - removed from backend
            // commit("setProducts", data.products_dataset?.data || []);  // Not used - removed from backend
            commit("setKpis", data.kpi_dataset?.data || []);
            commit("setServices", data.services_dataset?.data || []);
            commit("setServiceCategories", data.service_categories_dataset?.data || []);
            commit("setAppraisalPeriods", data.appraisal_period_dataset?.data || []);
            commit("setChecklists", data.checklist_dataset?.data || []);
            commit("setChecklistTasks", data.checklist_task_dataset?.data || []);
            commit("setChecklistSubtasks", data.checklist_subtask_dataset?.data || []);
            commit("setEmployeeSettings", data.employee_setting_dataset?.data || []);
            // Performance masters
            commit("setUoms", data.uom_dataset?.data || []);
            commit("setKpiTypes", data.kpi_type_dataset?.data || []);
            commit("setTargetTypes", data.target_type_dataset?.data || []);
            // commit("setChannels", data.channel_dataset?.data || []);  // Not used - commented out
            // commit("setSubChannels", data.subchannel_dataset?.data || []);  // Not used - commented out
            // commit("setDivisions", data.division_dataset?.data || []);  // Not used - commented out
            // commit("setCategories", data.category_dataset?.data || []);  // Not used - commented out
            // commit("setStores", data.store_dataset?.data || []);  // Not used - commented out
            commit("setPerformanceRatings", data.performance_rating_dataset?.data || []);
            commit("setFavouriteDetails", data.favourite_details_dataset?.data || {});
            commit("setFinancialsInputDetails", data.financials_input_details_dataset?.data || []);
            commit("setHolidays", data.holiday_dataset?.data || []);
            commit("setWeekoffs", data.weekoff_dataset?.data || []);
            commit("setTenantMasters", data.tenant_masters_dataset?.data || []);
            commit("setBranches", data.branches_dataset?.data || []);
            commit("setClientTypes", data.client_type_dataset?.data || []);
            commit("setPolicyTypes", data.policy_type_dataset?.data || []);
            commit("setRoutes", data.route_dataset?.data || []);
            commit("setTicketCategories", data.ticket_category_dataset?.data || []);
            commit("setLocations", data.location_dataset?.data || []);

            console.log("[MasterData] Successfully loaded base datasets");
         }

         // NOTE: FMS datasets are now loaded on-demand by individual FMS pages
         // using the fetchFMSDatasets action. This reduces initial load time
         // and only fetches data when needed.

         // Mark as loaded and persist
         commit("setLoaded", true);
         localStorage.setItem("MASTER_DATA_LOADED", "true");
         localStorage.setItem("MASTER_DATA_DOMAIN", domain);

         console.log("[MasterData] Master data loading complete");

      } catch (error) {
         console.error("[MasterData] Error fetching master data:", error);
         throw error;
      } finally {
         commit("setIsFetching", false);
      }
   },

   /**
    * Clears the cache and reloads master data
    * Useful when master data is updated (e.g., after CRUD operations)
    */
   async refreshMasterData({ commit }) {
      console.log("[MasterData] Refreshing master data...");
      commit("setLoaded", false);
      // Clear localStorage to force reload on next fetchMasterData call
      localStorage.removeItem("MASTER_DATA_LOADED");
      localStorage.removeItem("MASTER_DATA_DOMAIN");

      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing master data...", { entity_id, domain });

         // Guard: don't fetch if domain is missing
         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         // API Call 1: Base datasets - always loaded
         console.log("[MasterData] Refreshing base datasets...");
         const baseResponse = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: [
               "users_dataset",
               // "employees_dataset" removed — fetched per-screen via /employees API
               "departments_dataset",
               "designations_dataset",
               "subdepartments_dataset",
               "clients_dataset",
               "kpi_dataset",
               "appraisal_period_dataset",
               "checklist_dataset",
               "checklist_task_dataset",
               "checklist_subtask_dataset",
               "employee_setting_dataset",
               "uom_dataset",
               "kpi_type_dataset",
               "target_type_dataset",
               "performance_rating_dataset",
               "favourite_details_dataset",
               "financials_input_details_dataset",
               "holiday_dataset",
               "weekoff_dataset",
               "tenant_masters_dataset",
               "branches_dataset",
               "services_dataset",
               "service_categories_dataset"
            ],
         });

         if (baseResponse.data && baseResponse.data.status === "success") {
            const data = baseResponse.data.data;

            commit("setUsers", data.users_dataset?.data || []);
            // setEmployees removed — employees fetched per-screen via /employees API
            commit("setDepartments", data.departments_dataset?.data || []);
            commit("setDesignations", data.designations_dataset?.data || []);
            commit("setSubDepartments", data.subdepartments_dataset?.data || []);
            // commit("setFunctions", data.functions_dataset?.data || []);  // Not used - commented out
            // commit("setRoles", data.roles_dataset?.data || []);  // Not used - commented out
            commit("setClients", data.clients_dataset?.data || []);
            // commit("setIndustries", data.industries_dataset?.data || []);  // Not used - commented out
            // commit("setProcesses", data.processes_dataset?.data || []);  // Not used - removed from backend
            // commit("setProducts", data.products_dataset?.data || []);  // Not used - removed from backend
            commit("setKpis", data.kpi_dataset?.data || []);
            commit("setServices", data.services_dataset?.data || []);
            commit("setServiceCategories", data.service_categories_dataset?.data || []);
            commit("setAppraisalPeriods", data.appraisal_period_dataset?.data || []);
            commit("setChecklists", data.checklist_dataset?.data || []);
            commit("setChecklistTasks", data.checklist_task_dataset?.data || []);
            commit("setChecklistSubtasks", data.checklist_subtask_dataset?.data || []);
            commit("setEmployeeSettings", data.employee_setting_dataset?.data || []);
            // Performance masters
            commit("setUoms", data.uom_dataset?.data || []);
            commit("setKpiTypes", data.kpi_type_dataset?.data || []);
            commit("setTargetTypes", data.target_type_dataset?.data || []);
            // commit("setChannels", data.channel_dataset?.data || []);  // Not used - commented out
            // commit("setSubChannels", data.subchannel_dataset?.data || []);  // Not used - commented out
            // commit("setDivisions", data.division_dataset?.data || []);  // Not used - commented out
            // commit("setCategories", data.category_dataset?.data || []);  // Not used - commented out
            // commit("setStores", data.store_dataset?.data || []);  // Not used - commented out
            commit("setPerformanceRatings", data.performance_rating_dataset?.data || []);
            commit("setFavouriteDetails", data.favourite_details_dataset?.data || {});
            commit("setFinancialsInputDetails", data.financials_input_details_dataset?.data || []);
            commit("setHolidays", data.holiday_dataset?.data || []);
            commit("setWeekoffs", data.weekoff_dataset?.data || []);
            commit("setTenantMasters", data.tenant_masters_dataset?.data || []);
            commit("setBranches", data.branches_dataset?.data || []);

            console.log("[MasterData] Successfully refreshed base datasets");
         }

         // NOTE: FMS datasets are now loaded on-demand by individual FMS pages
         // using the fetchFMSDatasets action. This reduces initial load time
         // and only fetches data when needed.

         // Mark as loaded and persist
         commit("setLoaded", true);
         localStorage.setItem("MASTER_DATA_LOADED", "true");
         localStorage.setItem("MASTER_DATA_DOMAIN", domain);

         console.log("[MasterData] Master data refresh complete");

      } catch (error) {
         console.error("[MasterData] Error refreshing master data:", error);
         throw error;
      }
   },

   /**
    * Clears all master data and localStorage when user logs out
    * Call this action in the logout process to ensure fresh data load on next login
    */
   clearMasterDataCache({ commit }) {
      console.log("[MasterData] Clearing master data cache for logout");
      commit("clearCache");
      commit("setLoaded", false);
      commit("setIsFetching", false);
      // Clear localStorage to force reload on next login
      localStorage.removeItem("MASTER_DATA_LOADED");
      localStorage.removeItem("MASTER_DATA_DOMAIN");
      console.log("[MasterData] Master data cache cleared");
   },

   /**
    * Fetches services and service categories data on-demand.
    * Called from ticket pages when they need this data.
    */
   async fetchServicesData({ commit, state }) {
      // Skip if already loaded
      if (state.services.length > 0 && state.serviceCategories.length > 0) {
         console.log("[MasterData] Services data already loaded, skipping fetch");
         return;
      }

      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Fetching services data on-demand...");

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["services_dataset", "service_categories_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setServices", data.services_dataset?.data || []);
            commit("setServiceCategories", data.service_categories_dataset?.data || []);
            console.log("[MasterData] Successfully loaded services data");
         }
      } catch (error) {
         console.error("[MasterData] Error fetching services data:", error);
         throw error;
      }
   },

   /**
    * Fetches tickets data on-demand.
    * Called from ticket report pages when they need this data.
    * Uses the master_data API with Cachex backend.
    */
   async fetchTicketsData({ commit, state }) {
      // Skip if already loaded
      if (state.tickets.length > 0) {
         console.log("[MasterData] Tickets data already loaded, skipping fetch");
         return;
      }

      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Fetching tickets data on-demand...", { entity_id, domain });

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["tickets_dataset"],
         });

         console.log("[MasterData] API Response:", response.data);
         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            console.log("[MasterData] tickets_dataset:", data.tickets_dataset);
            commit("setTickets", data.tickets_dataset?.data || []);
            console.log("[MasterData] Successfully loaded tickets data");
         }
      } catch (error) {
         console.error("[MasterData] Error fetching tickets data:", error);
         throw error;
      }
   },

   /**
    * Fetches processes data on-demand.
    * Called from checklist template pages when they need process data.
    * Uses the master_data API with Cachex backend.
    */
   async fetchProcessesData({ commit, state }) {
      // Skip if already loaded
      if (state.processes.length > 0) {
         console.log("[MasterData] Processes data already loaded, skipping fetch");
         return;
      }

      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Fetching processes data on-demand...", { entity_id, domain });

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["process_dataset"],
         });

         console.log("[MasterData] API Response:", response.data);
         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            console.log("[MasterData] process_dataset:", data.process_dataset);
            commit("setProcesses", data.process_dataset?.data || []);
            console.log("[MasterData] Successfully loaded processes data");
         }
      } catch (error) {
         console.error("[MasterData] Error fetching processes data:", error);
         throw error;
      }
   },

   /**
    * Fetches industries data on-demand.
    * Called from checklist pages when they need industry data.
    * Uses the master_data API with Cachex backend.
    */
   async fetchIndustriesData({ commit, state }) {
      // Skip if already loaded
      if (state.industries.length > 0) {
         console.log("[MasterData] Industries data already loaded, skipping fetch");
         return;
      }

      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Fetching industries data on-demand...", { entity_id, domain });

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["industry_dataset"],
         });

         console.log("[MasterData] API Response:", response.data);
         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            console.log("[MasterData] industry_dataset:", data.industry_dataset);
            commit("setIndustries", data.industry_dataset?.data || []);
            console.log("[MasterData] Successfully loaded industries data");
         }
      } catch (error) {
         console.error("[MasterData] Error fetching industries data:", error);
         throw error;
      }
   },

   /**
    * Fetches checklist page data in a single API call.
    * Combines: forms_master, form_maintenance, process, industry datasets.
    * Called from act_checklist pages to load all required data at once.
    */
   async fetchChecklistPageData({ commit, state }) {
      // Skip if all data already loaded
      if (state.formsMasters.length > 0 && state.formMaintenances.length > 0 && state.processes.length > 0 && state.industries.length > 0) {
         console.log("[MasterData] Checklist page data already loaded, skipping fetch");
         return;
      }

      // Skip if already loading (prevent duplicate concurrent calls)
      if (state.checklistPageDataLoading) {
         console.log("[MasterData] Checklist page data already loading, skipping duplicate call");
         return;
      }

      try {
         // Set loading flag
         commit("setChecklistPageDataLoading", true);

         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         // Build flags array based on what's not loaded
         const flags = [];
         if (state.formsMasters.length === 0) flags.push("forms_master_dataset");
         if (state.formMaintenances.length === 0) flags.push("form_maintenance_dataset");
         if (state.processes.length === 0) flags.push("process_dataset");
         if (state.industries.length === 0) flags.push("industry_dataset");

         if (flags.length === 0) {
            console.log("[MasterData] All checklist page data already loaded");
            commit("setChecklistPageDataLoading", false);
            return;
         }

         console.log("[MasterData] Fetching checklist page data...", { entity_id, domain, flags });

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: flags,
         });

         console.log("[MasterData] API Response:", response.data);

         if (response.data && response.data.status === "success") {
            const data = response.data.data;

            console.log("[MasterData] forms_master_dataset:", data.forms_master_dataset);
            console.log("[MasterData] form_maintenance_dataset:", data.form_maintenance_dataset);
            console.log("[MasterData] process_dataset:", data.process_dataset);
            console.log("[MasterData] industry_dataset:", data.industry_dataset);

            if (data.forms_master_dataset) {
               commit("setFormsMasters", data.forms_master_dataset?.data || []);
            }
            if (data.form_maintenance_dataset) {
               commit("setFormMaintenances", data.form_maintenance_dataset?.data || []);
            }
            if (data.process_dataset) {
               commit("setProcesses", data.process_dataset?.data || []);
            }
            if (data.industry_dataset) {
               commit("setIndustries", data.industry_dataset?.data || []);
            }

            console.log("[MasterData] Successfully loaded checklist page data");
         }
      } catch (error) {
         console.error("[MasterData] Error fetching checklist page data:", error);
         throw error;
      } finally {
         // Reset loading flag
         commit("setChecklistPageDataLoading", false);
      }
   },

   /**
    * Refreshes only client types data after CRUD operations
    * Forces a fresh fetch from the API to update both Cachex (backend) and Vuex (frontend)
    * More efficient than refreshMasterData() as it only fetches client types
    */
   async refreshClientTypes({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing client types...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["client_type_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setClientTypes", data.client_type_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed client types:", data.client_type_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing client types:", error);
         throw error;
      }
   },

   async refreshPolicyTypes({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing policy types...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["policy_type_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setPolicyTypes", data.policy_type_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed policy types:", data.policy_type_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing policy types:", error);
         throw error;
      }
   },

   async refreshRoutes({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing routes...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["route_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setRoutes", data.route_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed routes:", data.route_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing routes:", error);
         throw error;
      }
   },

   async refreshTicketCategories({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing ticket categories...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["ticket_category_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setTicketCategories", data.ticket_category_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed ticket categories:", data.ticket_category_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing ticket categories:", error);
         throw error;
      }
   },

   async refreshLocations({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing locations...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["location_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setLocations", data.location_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed locations:", data.location_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing locations:", error);
         throw error;
      }
   },

   async refreshPaymentReceived({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing payment received...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["payment_received_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setPaymentReceived", data.payment_received_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed payment received:", data.payment_received_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing payment received:", error);
         throw error;
      }
   },

   async refreshCustomerGroups({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing customer groups...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["customer_group_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setCustomerGroups", data.customer_group_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed customer groups:", data.customer_group_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing customer groups:", error);
         throw error;
      }
   },

   async refreshServiceCategories({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing service categories...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["service_category_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setServiceCategories", data.service_category_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed service categories:", data.service_category_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing service categories:", error);
         throw error;
      }
   },

   async refreshServices({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing services...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["services_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setServices", data.services_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed services:", data.services_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing services:", error);
         throw error;
      }
   },

   async refreshDepartments({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing departments...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["departments_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setDepartments", data.departments_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed departments:", data.departments_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing departments:", error);
         throw error;
      }
   },

   async refreshSubDepartments({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing subdepartments...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["subdepartment_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setSubDepartments", data.subdepartment_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed subdepartments:", data.subdepartment_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing subdepartments:", error);
         throw error;
      }
   },

   async refreshDesignations({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing designations...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["designation_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setDesignations", data.designation_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed designations:", data.designation_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing designations:", error);
         throw error;
      }
   },

   async refreshUsers({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing users...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["users_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setUsers", data.users_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed users:", data.users_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing users:", error);
         throw error;
      }
   },

   // REMOVED: refreshEmployees action — employees are no longer cached in Vuex.
   // Components that need fresh employee data should call GET /employees
   // directly with their own entity_id/active filter (10k+ records make
   // client-side caching impractical).

   async refreshClients({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing clients...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["clients_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setClients", data.clients_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed clients:", data.clients_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing clients:", error);
         throw error;
      }
   },

   async refreshKpis({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing KPIs...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["kpi_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setKpis", data.kpi_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed KPIs:", data.kpi_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing KPIs:", error);
         throw error;
      }
   },

   async refreshKpiTypes({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing KPI types...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["kpi_type_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setKpiTypes", data.kpi_type_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed KPI types:", data.kpi_type_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing KPI types:", error);
         throw error;
      }
   },

   async refreshTargetTypes({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing target types...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["target_type_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setTargetTypes", data.target_type_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed target types:", data.target_type_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing target types:", error);
         throw error;
      }
   },

   async refreshUoms({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing UOMs...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["uom_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setUoms", data.uom_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed UOMs:", data.uom_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing UOMs:", error);
         throw error;
      }
   },

   async refreshPerformanceRatings({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing performance ratings...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["performance_rating_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setPerformanceRatings", data.performance_rating_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed performance ratings:", data.performance_rating_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing performance ratings:", error);
         throw error;
      }
   },

   async refreshAppraisalDetails({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing appraisal details...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["appraisal_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setAppraisalDetails", data.appraisal_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed appraisal details:", data.appraisal_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing appraisal details:", error);
         throw error;
      }
   },

   async refreshAppraisalPeriods({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing appraisal periods...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["appraisal_period_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setAppraisalPeriods", data.appraisal_period_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed appraisal periods:", data.appraisal_period_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing appraisal periods:", error);
         throw error;
      }
   },

   async refreshChecklists({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing checklists...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["checklist_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setChecklists", data.checklist_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed checklists:", data.checklist_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing checklists:", error);
         throw error;
      }
   },

   async refreshChecklistTasks({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing checklist tasks...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["checklist_task_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setChecklistTasks", data.checklist_task_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed checklist tasks:", data.checklist_task_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing checklist tasks:", error);
         throw error;
      }
   },

   async refreshChecklistSubtasks({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing checklist subtasks...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["checklist_subtask_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setChecklistSubtasks", data.checklist_subtask_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed checklist subtasks:", data.checklist_subtask_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing checklist subtasks:", error);
         throw error;
      }
   },

   async refreshEmployeeSettings({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing employee settings...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["employee_setting_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setEmployeeSettings", data.employee_setting_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed employee settings:", data.employee_setting_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing employee settings:", error);
         throw error;
      }
   },

   async refreshHolidays({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing holidays...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["holiday_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setHolidays", data.holiday_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed holidays:", data.holiday_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing holidays:", error);
         throw error;
      }
   },

   async refreshWeekoffs({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing weekoffs...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["weekoff_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setWeekoffs", data.weekoff_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed weekoffs:", data.weekoff_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing weekoffs:", error);
         throw error;
      }
   },

   async refreshTenantMasters({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing tenant masters...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["tenant_masters_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setTenantMasters", data.tenant_masters_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed tenant masters:", data.tenant_masters_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing tenant masters:", error);
         throw error;
      }
   },

   async refreshBranches({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing branches...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["branches_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setBranches", data.branches_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed branches:", data.branches_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing branches:", error);
         throw error;
      }
   },

   async refreshTickets({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing tickets...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["tickets_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setTickets", data.tickets_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed tickets:", data.tickets_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing tickets:", error);
         throw error;
      }
   },

   async refreshFormMaintenances({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing form maintenances...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["form_maintenance_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setFormMaintenances", data.form_maintenance_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed form maintenances:", data.form_maintenance_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing form maintenances:", error);
         throw error;
      }
   },

   async refreshFormsMasters({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing forms masters...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["forms_master_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setFormsMasters", data.forms_master_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed forms masters:", data.forms_master_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing forms masters:", error);
         throw error;
      }
   },

   async refreshSlabs({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing slabs...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["slab_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setSlabs", data.slab_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed slabs:", data.slab_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing slabs:", error);
         throw error;
      }
   },

   async refreshProcesses({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing processes...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["process_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setProcesses", data.process_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed processes:", data.process_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing processes:", error);
         throw error;
      }
   },

   async refreshIndustries({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing industries...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["industry_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setIndustries", data.industry_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed industries:", data.industry_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing industries:", error);
         throw error;
      }
   },

   async refreshCustomerGrades({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing customer grades...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["customer_grade_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setCustomerGrades", data.customer_grade_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed customer grades:", data.customer_grade_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing customer grades:", error);
         throw error;
      }
   },

   async refreshFavouriteDetails({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing favourite details...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["favourite_details_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setFavouriteDetails", data.favourite_details_dataset?.data || {});
            console.log("[MasterData] Successfully refreshed favourite details");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing favourite details:", error);
         throw error;
      }
   },

   async refreshFinancialsInputDetails({ commit }) {
      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing financials input details...", { entity_id, domain });

         if (!domain) {
            console.warn("[MasterData] No domain found in localStorage, skipping refresh");
            return;
         }

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: ["financials_input_details_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setFinancialsInputDetails", data.financials_input_details_dataset?.data || []);
            console.log("[MasterData] Successfully refreshed financials input details:", data.financials_input_details_dataset?.data?.length || 0, "records");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing financials input details:", error);
         throw error;
      }
   },

   /**
    * Fetches FMS datasets on-demand with intelligent caching.
    * Only fetches datasets that are not already loaded in Vuex.
    * Called from FMS pages to load required master data.
    *
    * Available FMS datasets:
    * - client_type_dataset (Client Types)
    * - policy_type_dataset (Policy Types)
    * - route_dataset (Routes)
    * - slab_dataset (Slabs)
    * - payment_received_dataset (Payment Received)
    * - ticket_category_dataset (Ticket Categories)
    * - location_dataset (Locations)
    * - customer_group_dataset (Customer Groups)
    * - customer_grade_dataset (Customer Grades)
    *
    * @param {Object} context - Vuex context
    * @param {Array} requestedFlags - Array of dataset flags to fetch (e.g., ['client_type_dataset', 'route_dataset'])
    */
   async fetchFMSDatasets({ commit, state }, requestedFlags = []) {
      if (!requestedFlags || requestedFlags.length === 0) {
         console.log("[MasterData] No FMS datasets requested");
         return;
      }

      // Map of dataset flags to their state properties and whether they're loaded
      const datasetMap = {
         'client_type_dataset': {
            stateKey: 'clientTypes',
            commitName: 'setClientTypes',
            isLoaded: () => state.clientTypes.length > 0
         },
         'policy_type_dataset': {
            stateKey: 'policyTypes',
            commitName: 'setPolicyTypes',
            isLoaded: () => state.policyTypes.length > 0
         },
         'route_dataset': {
            stateKey: 'routes',
            commitName: 'setRoutes',
            isLoaded: () => state.routes.length > 0
         },
         'slab_dataset': {
            stateKey: 'slabs',
            commitName: 'setSlabs',
            isLoaded: () => state.slabs.length > 0
         },
         'payment_received_dataset': {
            stateKey: 'paymentReceived',
            commitName: 'setPaymentReceived',
            isLoaded: () => state.paymentReceived.length > 0
         },
         'ticket_category_dataset': {
            stateKey: 'ticketCategories',
            commitName: 'setTicketCategories',
            isLoaded: () => state.ticketCategories.length > 0
         },
         'location_dataset': {
            stateKey: 'locations',
            commitName: 'setLocations',
            isLoaded: () => state.locations.length > 0
         },
         'customer_group_dataset': {
            stateKey: 'customerGroups',
            commitName: 'setCustomerGroups',
            isLoaded: () => state.customerGroups.length > 0
         },
         'customer_grade_dataset': {
            stateKey: 'customerGrades',
            commitName: 'setCustomerGrades',
            isLoaded: () => state.customerGrades.length > 0
         },
      };

      // Filter out datasets that are already loaded
      const flagsToFetch = requestedFlags.filter(flag => {
         const dataset = datasetMap[flag];
         if (!dataset) {
            console.warn(`[MasterData] Unknown FMS dataset flag: ${flag}`);
            return false;
         }
         return !dataset.isLoaded();
      });

      // If all requested datasets are already loaded, skip API call
      if (flagsToFetch.length === 0) {
         console.log("[MasterData] All requested FMS datasets already loaded, skipping fetch", requestedFlags);
         return;
      }

      try {
         const entity_id = localStorage.getItem("ENTITY_ID");
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Fetching FMS datasets...", { entity_id, domain, flagsToFetch });

         const response = await api.post("master_data", {
            entity_id: entity_id ? parseInt(entity_id) : null,
            sub_domain: domain,
            flags: flagsToFetch,
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;

            // Commit each fetched dataset to Vuex
            flagsToFetch.forEach(flag => {
               const dataset = datasetMap[flag];
               if (data[flag]) {
                  commit(dataset.commitName, data[flag]?.data || []);
                  console.log(`[MasterData] Successfully loaded ${flag}:`, data[flag]?.data?.length || 0, 'records');
               }
            });

            console.log("[MasterData] Successfully loaded FMS datasets");
         }
      } catch (error) {
         console.error("[MasterData] Error fetching FMS datasets:", error);
         throw error;
      }
   },

   /**
    * Fetches financial ratios data filtered by country and industry.
    * This is kept for on-demand loading when component needs it.
    *
    * @param {Object} context - Vuex context
    * @param {Object} params - { country, industry }
    */
   async fetchFinancialRatios({ commit, state }, { country, industry }) {
      // Skip if already loaded with same params
      if (state.financialRatiosLoaded && state.financialRatios.length > 0) {
         console.log("[MasterData] Financial ratios already loaded, skipping fetch");
         return;
      }

      if (!country || !industry) {
         console.warn("[MasterData] Country or industry not provided for financial ratios");
         return;
      }

      try {
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Fetching financial ratios...", { country, industry });

         const response = await api.post("master_data", {
            sub_domain: domain,
            country: country,
            industry: industry,
            flags: ["financial_ratios_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setFinancialRatios", data.financial_ratios_dataset?.data || []);
            commit("setFinancialRatiosLoaded", true);
            console.log("[MasterData] Successfully loaded financial ratios");
         }
      } catch (error) {
         console.error("[MasterData] Error fetching financial ratios:", error);
         throw error;
      }
   },

   /**
    * Refreshes financial ratios data (clears and reloads)
    */
   async refreshFinancialRatios({ commit }, { country, industry }) {
      commit("setFinancialRatiosLoaded", false);
      commit("setFinancialRatios", []);

      if (!country || !industry) {
         console.warn("[MasterData] Country or industry not provided for financial ratios refresh");
         return;
      }

      try {
         const domain = localStorage.getItem("domain");

         console.log("[MasterData] Refreshing financial ratios...", { country, industry });

         const response = await api.post("master_data", {
            sub_domain: domain,
            country: country,
            industry: industry,
            flags: ["financial_ratios_dataset"],
         });

         if (response.data && response.data.status === "success") {
            const data = response.data.data;
            commit("setFinancialRatios", data.financial_ratios_dataset?.data || []);
            commit("setFinancialRatiosLoaded", true);
            console.log("[MasterData] Successfully refreshed financial ratios");
         }
      } catch (error) {
         console.error("[MasterData] Error refreshing financial ratios:", error);
         throw error;
      }
   },
};

const mutations = {
   setUsers(state, data) {
      state.users = [...data].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   // setEmployees mutation REMOVED — employees are no longer stored in Vuex.
   // Fetched per-screen via GET /employees?entity_id=X&active=true to avoid
   // 10k+ record memory bloat.
   setDepartments(state, data) {
      state.departments = [...data].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setDesignations(state, data) {
      state.designations = [...data].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setSubDepartments(state, data) {
      state.subDepartments = [...data].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setClients(state, data) {
      state.clients = [...data].sort((a, b) =>
         (a.client_name || "").trim().localeCompare((b.client_name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setIndustries(state, data) {
      state.industries = [...(data || [])].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setProcesses(state, data) {
      state.processes = [...(data || [])].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setKpis(state, data) {
      state.kpis = [...data].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setServices(state, data) {
      state.services = [...(data || [])].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setServiceCategories(state, data) {
      state.serviceCategories = [...(data || [])].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setAppraisalDetails(state, data) {
      state.appraisalDetails = [...data].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setAppraisalPeriods(state, data) {
      state.appraisalPeriods = [...data].sort((a, b) =>
         (b.year || "").localeCompare((a.year || ""), undefined, { sensitivity: "base" })
      );
   },
   setChecklists(state, data) {
      state.checklists = [...data].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setChecklistTasks(state, data) {
      state.checklistTasks = [...data].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setChecklistSubtasks(state, data) {
      state.checklistSubtasks = [...data].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setEmployeeSettings(state, data) {
      // Employee settings are configuration data, keep original order
      state.employeeSettings = data;
   },
   // Performance masters mutations
   setUoms(state, data) {
      state.uoms = [...data].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setKpiTypes(state, data) {
      state.kpiTypes = [...data].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setTargetTypes(state, data) {
      state.targetTypes = [...data].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setPerformanceRatings(state, data) {
      state.performanceRatings = [...data].sort((a, b) =>
         (a.rating || "").toString().localeCompare((b.rating || "").toString(), undefined, { sensitivity: "base" })
      );
   },
   setFinancialRatios(state, data) {
      // Keep original order for financial ratios
      state.financialRatios = data;
   },
   setFinancialRatiosLoaded(state, val) {
      state.financialRatiosLoaded = val;
   },
   setFavouriteDetails(state, data) {
      // Favourite details is an object, not an array
      state.favouriteDetails = data || {};
   },
   setFinancialsInputDetails(state, data) {
      // Keep original order for financials input details
      state.financialsInputDetails = data || [];
   },
   setHolidays(state, data) {
      // Keep original order for holidays (sorted by from_date)
      state.holidays = data || [];
   },
   setWeekoffs(state, data) {
      // Keep original order for weekoffs (sorted by day_of_the_week)
      state.weekoffs = data || [];
   },
   setTenantMasters(state, data) {
      // Keep original order for tenant masters (sorted by id desc)
      state.tenantMasters = data || [];
   },
   setBranches(state, data) {
      state.branches = [...(data || [])].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setTickets(state, data) {
      state.tickets = [...(data || [])].sort((a, b) =>
         (a.tkt_id || "").trim().localeCompare((b.tkt_id || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setFormMaintenances(state, data) {
      state.formMaintenances = [...(data || [])].sort((a, b) =>
         (a.form_name || "").trim().localeCompare((b.form_name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setFormsMasters(state, data) {
      state.formsMasters = [...(data || [])].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setSlabs(state, data) {
      // Keep original order for slabs (sorted by id)
      state.slabs = data || [];
   },
   setProcesses(state, data) {
      state.processes = [...(data || [])].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setIndustries(state, data) {
      state.industries = [...(data || [])].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setClientTypes(state, data) {
      state.clientTypes = [...(data || [])].sort((a, b) =>
         (a.clitype || "").trim().localeCompare((b.clitype || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setPolicyTypes(state, data) {
      state.policyTypes = [...(data || [])].sort((a, b) =>
         (a.ptype || "").trim().localeCompare((b.ptype || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setRoutes(state, data) {
      state.routes = [...(data || [])].sort((a, b) =>
         (a.routeno || "").trim().localeCompare((b.routeno || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setPaymentReceived(state, data) {
      state.paymentReceived = [...(data || [])].sort((a, b) =>
         (a.payment_received || "").trim().localeCompare((b.payment_received || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setTicketCategories(state, data) {
      state.ticketCategories = [...(data || [])].sort((a, b) =>
         (a.tcatg || "").trim().localeCompare((b.tcatg || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setLocations(state, data) {
      state.locations = [...(data || [])].sort((a, b) =>
         (a.location || "").trim().localeCompare((b.location || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setCustomerGroups(state, data) {
      state.customerGroups = [...(data || [])].sort((a, b) =>
         (a.custgrp || "").trim().localeCompare((b.custgrp || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setCustomerGrades(state, data) {
      state.customerGrades = [...(data || [])].sort((a, b) =>
         (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
      );
   },
   setLoaded(state, val) {
      state.loaded = val;
   },
   setChecklistPageDataLoading(state, val) {
      state.checklistPageDataLoading = val;
   },
   setIsFetching(state, val) {
      state.isFetching = val;
   },

   /**
    * Clear all cached data
    */
   clearCache(state) {
      state.users = [];
      // state.employees = []; — field removed from state
      state.departments = [];
      state.designations = [];
      state.subDepartments = [];
      // state.functions = [];  // Not used - commented out
      // state.roles = [];  // Not used - commented out
      state.clients = [];
      // state.industries = [];  // Not used - commented out
      state.processes = [];
      state.products = [];
      state.kpis = [];
      state.services = [];
      state.serviceCategories = [];
      state.appraisalDetails = [];
      state.appraisalPeriods = [];
      state.checklists = [];
      state.checklistTasks = [];
      state.checklistSubtasks = [];
      state.employeeSettings = [];
      // Performance masters
      state.uoms = [];
      state.kpiTypes = [];
      state.targetTypes = [];
      // state.channels = [];  // Not used - commented out
      // state.subChannels = [];  // Not used - commented out
      // state.divisions = [];  // Not used - commented out
      // state.categories = [];  // Not used - commented out
      // state.stores = [];  // Not used - commented out
      state.performanceRatings = [];
      // Financial ratios
      state.financialRatios = [];
      state.financialRatiosLoaded = false;
      // Favourite Details
      state.favouriteDetails = {};
      // Financials Input Details
      state.financialsInputDetails = [];
      // Holidays
      state.holidays = [];
      // Weekoffs
      state.weekoffs = [];
      // Tenant Masters
      state.tenantMasters = [];
      // Branches
      state.branches = [];
      // Tickets
      state.tickets = [];
      // Form Maintenance
      state.formMaintenances = [];
      // Forms Master
      state.formsMasters = [];
      // Slabs
      state.slabs = [];
      // Processes
      state.processes = [];
      // Industries
      state.industries = [];
      // Client Types
      state.clientTypes = [];
      // Policy Types
      state.policyTypes = [];
      // Routes
      state.routes = [];
      // Payment Received
      state.paymentReceived = [];
      // Ticket Categories
      state.ticketCategories = [];
      // Locations
      state.locations = [];
      // Customer Groups
      state.customerGroups = [];
      // Customer Grades
      state.customerGrades = [];
      state.loaded = false;
   },
};

export default {
   namespaced: true,
   state,
   getters,
   actions,
   mutations,
};
